/**
 * Composable: useAdminDashboardConfig
 *
 * Fetches admin_dashboard_settings via GET /admin/config/settings/admin_dashboard_settings
 * and produces normalized, validated sections, periods, and navigation links.
 *
 * The admin config settings endpoint returns a full setting record:
 *   { key, name, description, type, category, value: { enabled, sections, ... } }
 *
 * In multitenant mode that endpoint is for platform administrators only. A
 * tenant administrator reads the same value from the authenticated settings,
 * which are already loaded, so the overview sends no request it would be refused.
 */
import { computed, ref } from 'vue';
import { useHCommon } from './use-page-common';
import { getAdministrationPolicy } from './use-administration-features';
import { defaultSettings } from './use-settings';
import { widgetId } from '../utils/dashboard-query-utils.js';
import { isAdminLinkAvailable, isApiPathAvailable } from '../utils/administration-features';

/**
 * Normalizes API payloads to the effective dashboard config object.
 * Supports both direct config payloads and setting-record payloads with nested `value`.
 */
function unwrapDashboardConfig(payload) {
  let cfg = payload;

  // Common shape from settings endpoint: { key, ..., value: <config|string> }
  if (cfg && typeof cfg === 'object' && Object.prototype.hasOwnProperty.call(cfg, 'value')) {
    cfg = cfg.value;
  }

  // Some APIs store the full setting record as JSON string in `value`.
  if (typeof cfg === 'string') {
    cfg = JSON.parse(cfg);
  }

  // If the parsed object is still a setting record, unwrap once more.
  if (cfg && typeof cfg === 'object' && Object.prototype.hasOwnProperty.call(cfg, 'value')) {
    cfg = cfg.value;
  }

  return cfg ?? null;
}

/**
 * Assigns stable _id fields to all widgets (and breakdown items) in-place.
 */
/**
 * Drops what points at an unavailable administration page or Hub API endpoint
 * (a disabled feature, or guides, forms and dashboards administration in
 * multitenant mode), so the overview neither shows it nor requests its data.
 * A widget goes when its own URL or link is unavailable, or when every list
 * source or breakdown item it has is.
 */
function withoutUnavailable(widget, policy) {
  const apiAvailable = (url) => !url || isApiPathAvailable(url, policy);
  const linkAvailable = (link) => !link || isAdminLinkAvailable(link, policy);

  if (!apiAvailable(widget.url) || !apiAvailable(widget.trend?.url) || !linkAvailable(widget.link)) {
    return null;
  }

  // The widget object is kept unless something is removed from it, so the
  // overview does not re-render widgets whose content did not change.
  let result = widget;
  for (const field of ['items', 'sources']) {
    if (Array.isArray(widget[field]) && widget[field].length > 0) {
      const available = widget[field].filter((entry) => apiAvailable(entry?.url));
      if (available.length === 0) {
        return null;
      }
      if (available.length !== widget[field].length) {
        result = { ...result, [field]: available };
      }
    }
  }
  if (Array.isArray(widget.actions)) {
    const actions = widget.actions.filter((action) => linkAvailable(action?.link));
    if (actions.length !== widget.actions.length) {
      result = { ...result, actions };
    }
  }

  return result;
}

function assignIds(sections) {
  for (const section of sections) {
    for (const widget of section.children || []) {
      widget._id = widgetId(section.name, widget.name);
      // Breakdown items also get IDs
      if (widget.items) {
        for (const item of widget.items) {
          item._id = widgetId(section.name, `${widget.name}_${item.name}`);
        }
      }
    }
  }
  return sections;
}

function loadFromSettings({ rawConfig, error, loading }) {
  const value = defaultSettings.admin_dashboard_settings;
  if (value === undefined || value === null || value === '') {
    error.value = 'admin_dashboard_settings was not found in the API';
  } else {
    try {
      rawConfig.value = unwrapDashboardConfig(value);
    } catch (e) {
      error.value = e?.message ?? 'Failed to parse admin_dashboard_settings';
    }
  }
  loading.value = false;
}

export function useAdminDashboardConfig() {
  const { hHttpRequest } = useHCommon();

  const loading = ref(true);
  const error = ref(null);
  const rawConfig = ref(null);

  const startPolicy = getAdministrationPolicy();
  if (startPolicy.multitenant && !startPolicy.platformAuthority) {
    loadFromSettings({ rawConfig, error, loading });
  } else {
    fetchFromAdminSettings({ hHttpRequest, rawConfig, error, loading });
  }

  return useDashboardConfigState({ rawConfig, loading, error });
}

function fetchFromAdminSettings({ hHttpRequest, rawConfig, error, loading }) {
  const { data, execute } = hHttpRequest(
    { url: '/admin/config/settings/admin_dashboard_settings', method: 'get', immediate: false },
    { throwHttpErrors: false },
  );

  void execute()
    .then((response) => {
      if (!response) {
        error.value = 'Failed to load admin_dashboard_settings';
        return;
      }

      if (response.status === 404) {
        error.value = 'admin_dashboard_settings was not found in the API';
        return;
      }

      if (!response.ok) {
        error.value = `Failed to load admin_dashboard_settings (${response.status})`;
        return;
      }

      if (!data.value) {
        error.value = 'admin_dashboard_settings returned an empty response';
        return;
      }

      try {
        rawConfig.value = unwrapDashboardConfig(data.value);
      } catch (e) {
        error.value = e?.message ?? 'Failed to parse admin_dashboard_settings';
      }
    })
    .catch((e) => {
      error.value = e?.message ?? 'Failed to load admin_dashboard_settings';
    })
    .finally(() => {
      loading.value = false;
    });
}

function useDashboardConfigState({ rawConfig, loading, error }) {
  const config = computed(() => {
    try {
      return rawConfig.value;
    } catch {
      return null;
    }
  });

  const isEnabled = computed(() => config.value?.enabled !== false);

  const periods = computed(
    () =>
      config.value?.periods ?? [
        { label: 'Today', value: '1d', prevPeriod: '2d' },
        { label: '7d', value: '7d', prevPeriod: '14d' },
        { label: '30d', value: '30d', prevPeriod: '60d' },
        { label: '90d', value: '90d', prevPeriod: '180d' },
      ],
  );

  const defaultPeriod = computed(() => config.value?.defaultPeriod ?? '30d');

  const administrationPolicy = computed(() => getAdministrationPolicy());

  const enabledSections = computed(() => {
    if (!isEnabled.value || !config.value?.sections) return [];
    const sections = config.value.sections
      .filter((s) => s.enabled !== false)
      .map((s) => ({
        ...s,
        children: (s.children || [])
          .filter((w) => w.enabled !== false)
          .map((w) => withoutUnavailable(w, administrationPolicy.value))
          .filter(Boolean),
      }))
      .filter((s) => s.children.length > 0);
    return assignIds(sections);
  });

  const hasSections = computed(() => enabledSections.value.length > 0);

  const navigationLinks = computed(() =>
    (config.value?.navigation ?? []).filter((link) => {
      const target = link?.href ?? link?.url;
      return !target || isAdminLinkAvailable(target, administrationPolicy.value);
    }),
  );

  return {
    loading,
    error,
    isEnabled,
    periods,
    defaultPeriod,
    enabledSections,
    hasSections,
    navigationLinks,
  };
}
